/**
 * Mantenimiento → Activos (`/mantenimiento/activos`).
 *
 * Puerto de `Maintenance::assets/store_asset/update_asset/toggle_asset_status/
 * view_asset` + `views/maintenance/assets/{index,view}.php`: el listado de
 * equipos con su buscador, su filtro por categoría y su estado.
 *
 * La tabla del panel anterior tenía las mismas columnas (código, nombre con
 * serie, categoría, modelo, ubicación, sub-activos, estado y acciones); el
 * detalle, que era una pantalla aparte, aquí es un diálogo con los sub-activos y
 * su consumo.
 */

import {
  ArrowLeft,
  Ban,
  Boxes,
  Eye,
  Filter,
  Layers,
  Pencil,
  Plus,
  RotateCcw,
  Search,
  ShieldAlert,
} from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Alert, ICONO_EN_ALERTA } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Select } from '@/components/ui/select';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { ApiError } from '@/lib/http/api-error';
import { cn, formatNumber } from '@/lib/utils';

import { useActivos, useActivosCatalogos, useCambiarEstadoActivo, useCrearActivo, useActualizarActivo } from '../activos.api';
import type { Activo, ActivoPayload } from '../activos.types';
import { ActivoDetalle } from '../components/activo-detalle';
import { ActivoDialogo, CONTROL_ACTIVO } from '../components/activo-dialogo';

/** Filtro de estado del listado. */
type FiltroEstado = 'todos' | 'activos' | 'desuso';

/** Paginación amplia: cada estación tiene pocos activos y se filtra en el cliente. */
const POR_PAGINA = 200;

/** Mensaje legible de un error (de la API o de red). */
function mensajeDe(error: unknown, porDefecto: string): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;

  return porDefecto;
}

export function ActivosPage() {
  useDocumentTitle('Mantenimiento · Activos');

  const [busqueda, setBusqueda] = useState('');
  const [categoriaId, setCategoriaId] = useState(0);
  const [estado, setEstado] = useState<FiltroEstado>('todos');
  const [dialogo, setDialogo] = useState<{ activo: Activo | null } | null>(null);
  const [detalleId, setDetalleId] = useState<number | null>(null);
  const [aCambiar, setACambiar] = useState<Activo | null>(null);
  const [errorDialogo, setErrorDialogo] = useState<string | null>(null);
  const [errorConfirmacion, setErrorConfirmacion] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  const activos = useActivos({ perPage: POR_PAGINA });
  const catalogos = useActivosCatalogos();
  const crear = useCrearActivo();
  const actualizar = useActualizarActivo();
  const cambiarEstado = useCambiarEstadoActivo();

  const sinPermiso = activos.error instanceof ApiError && activos.error.status === 403;
  const lista = activos.data?.activos ?? [];
  const total = activos.data?.total ?? 0;
  const categorias = catalogos.data?.categorias ?? [];

  const termino = busqueda.trim().toLowerCase();
  const filtrados = lista.filter((activo) => {
    if (estado === 'activos' && !activo.activo) return false;
    if (estado === 'desuso' && activo.activo) return false;
    if (categoriaId && activo.categoria_id !== categoriaId) return false;

    if (!termino) return true;

    return (
      activo.codigo.toLowerCase().includes(termino) ||
      activo.nombre.toLowerCase().includes(termino) ||
      activo.serie.toLowerCase().includes(termino)
    );
  });

  const vigentes = lista.filter((activo) => activo.activo).length;
  const guardando = crear.isPending || actualizar.isPending;

  /** Guarda el alta o la edición según el diálogo abierto. */
  function guardar(payload: ActivoPayload) {
    setErrorDialogo(null);

    const comun = {
      onSuccess: (respuesta: { mensaje: string }) => {
        setAviso(respuesta.mensaje);
        setDialogo(null);
      },
      onError: (error: unknown) => setErrorDialogo(mensajeDe(error, 'La API no respondió.')),
    };

    if (dialogo?.activo) {
      actualizar.mutate({ id: dialogo.activo.id, payload }, comun);
      return;
    }

    crear.mutate(payload, comun);
  }

  /** Activa o marca en desuso el activo pendiente de confirmación. */
  function confirmarEstado() {
    if (!aCambiar) return;

    setErrorConfirmacion(null);

    cambiarEstado.mutate(
      { id: aCambiar.id, activo: !aCambiar.activo },
      {
        onSuccess: (respuesta) => {
          setAviso(respuesta.mensaje);
          setACambiar(null);
        },
        onError: (error) => setErrorConfirmacion(mensajeDe(error, 'La API no respondió.')),
      },
    );
  }

  const cabecera = (
    <header className="flex flex-wrap items-start justify-between gap-4">
      <div>
        <Link
          to="/inicio/mantenimiento"
          className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-ink-400 transition-colors hover:text-brand-600"
        >
          <ArrowLeft className="size-3" aria-hidden="true" />
          Mantenimiento
        </Link>
        <h1 className="mt-1 flex items-center gap-2 text-2xl font-bold tracking-tight text-ink-900">
          <Layers className="size-6 text-brand-500" aria-hidden="true" />
          Activos
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-ink-500">
          Equipos que se mantienen, agrupados por categoría. Cada activo puede tener sub-activos,
          que son los que llevan el control del umbral de galonaje.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {activos.data ? (
          <Badge variant="outline">
            {total} activos · {vigentes} vigentes
          </Badge>
        ) : null}
        <Button
          size="sm"
          leftIcon={<Plus className="size-3.5" aria-hidden="true" />}
          disabled={sinPermiso || categorias.length === 0}
          onClick={() => {
            setErrorDialogo(null);
            setDialogo({ activo: null });
          }}
        >
          Nuevo activo
        </Button>
      </div>
    </header>
  );

  if (sinPermiso) {
    return (
      <div className="space-y-6">
        {cabecera}
        <Alert variant="warning" title="Acceso restringido">
          <span className={ICONO_EN_ALERTA}>
            <ShieldAlert className="size-4" aria-hidden="true" />
            Esta pantalla es del grupo <strong>maintenance</strong>.
          </span>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {cabecera}

      {aviso ? <Alert variant="success">{aviso}</Alert> : null}

      <Card>
        <CardContent className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-2">
            <Input
              containerClassName="min-w-56 flex-1"
              leftIcon={<Search className="size-4" aria-hidden="true" />}
              placeholder="Buscar por código, nombre o número de serie…"
              value={busqueda}
              onChange={(evento) => setBusqueda(evento.target.value)}
            />

            <Select
              aria-label="Filtrar por categoría"
              className={cn(CONTROL_ACTIVO, 'w-auto min-w-40')}
              value={String(categoriaId)}
              onChange={(evento) => setCategoriaId(Number(evento.target.value))}
            >
              <option value="0">Todas las categorías</option>
              {categorias.map((categoria) => (
                <option key={categoria.id} value={categoria.id}>
                  {categoria.nombre}
                  {categoria.activa ? '' : ' (inactiva)'} · {categoria.activos}
                </option>
              ))}
            </Select>

            <div className="flex items-center gap-1 rounded-control border border-ink-200 bg-white p-1">
              <Filter className="mx-1 size-3.5 text-ink-400" aria-hidden="true" />
              {(['todos', 'activos', 'desuso'] as const).map((opcion) => (
                <button
                  key={opcion}
                  type="button"
                  className={cn(
                    'rounded-md px-3 py-1.5 text-xs font-semibold transition-colors',
                    estado === opcion
                      ? 'bg-brand-50 text-brand-700'
                      : 'text-ink-500 hover:bg-ink-50 hover:text-ink-700',
                  )}
                  onClick={() => setEstado(opcion)}
                >
                  {opcion === 'desuso' ? 'en desuso' : opcion}
                </button>
              ))}
            </div>
          </div>

          {catalogos.data && categorias.length === 0 ? (
            <Alert variant="warning" title="Sin categorías">
              Antes de registrar activos{' '}
              <Link to="/mantenimiento/categorias" className="font-semibold underline">
                crea una categoría
              </Link>
              .
            </Alert>
          ) : null}

          {activos.isLoading ? (
            <div className="flex flex-col gap-2">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : activos.error ? (
            <Alert variant="error" title="No se pudieron leer los activos">
              {mensajeDe(activos.error, 'La API no respondió.')}
            </Alert>
          ) : filtrados.length === 0 ? (
            <Alert variant="info" title="Sin resultados">
              {termino || estado !== 'todos' || categoriaId
                ? 'Ningún activo coincide con el filtro.'
                : 'Todavía no hay activos registrados.'}
            </Alert>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-ink-100 text-xs font-semibold uppercase tracking-wider text-ink-400">
                    <th className="py-2">Código</th>
                    <th className="py-2">Activo</th>
                    <th className="py-2">Categoría</th>
                    <th className="py-2">Modelo</th>
                    <th className="py-2">Ubicación</th>
                    <th className="py-2">Sub-activos</th>
                    <th className="py-2">Estado</th>
                    <th className="py-2 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filtrados.map((activo) => (
                    <tr key={activo.id} className="border-b border-ink-50 last:border-0">
                      <td className="py-3">
                        <span className="font-mono text-xs text-ink-700">{activo.codigo}</span>
                      </td>
                      <td className="py-3">
                        <span className="block font-semibold text-ink-900">{activo.nombre}</span>
                        <span className="block text-[11px] text-ink-400">
                          {activo.serie || 'Sin serie'}
                        </span>
                      </td>
                      <td className="py-3 text-ink-600">{activo.categoria}</td>
                      <td className="py-3 text-ink-600">
                        {activo.modelo || <span className="text-ink-300">—</span>}
                      </td>
                      <td className="py-3 text-ink-600">
                        {activo.ubicacion || <span className="text-ink-300">—</span>}
                      </td>
                      <td className="py-3">
                        <Badge variant={activo.sub_activos_activos > 0 ? 'brand' : 'neutral'}>
                          <Boxes className="size-3" aria-hidden="true" />
                          {formatNumber(activo.sub_activos_activos)}
                          {activo.sub_activos !== activo.sub_activos_activos ? (
                            <span className="text-ink-400">
                              /{formatNumber(activo.sub_activos)}
                            </span>
                          ) : null}
                        </Badge>
                      </td>
                      <td className="py-3">
                        <Badge variant={activo.activo ? 'success' : 'neutral'}>
                          {activo.activo ? 'Activo' : 'En desuso'}
                        </Badge>
                      </td>
                      <td className="py-3">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            title="Ver detalle"
                            aria-label={`Ver detalle de ${activo.nombre}`}
                            onClick={() => setDetalleId(activo.id)}
                          >
                            <Eye className="size-4" aria-hidden="true" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            title="Editar"
                            aria-label={`Editar ${activo.nombre}`}
                            onClick={() => {
                              setErrorDialogo(null);
                              setDialogo({ activo });
                            }}
                          >
                            <Pencil className="size-4" aria-hidden="true" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            title={activo.activo ? 'Marcar en desuso' : 'Reactivar'}
                            aria-label={
                              activo.activo
                                ? `Marcar ${activo.nombre} en desuso`
                                : `Reactivar ${activo.nombre}`
                            }
                            onClick={() => {
                              setErrorConfirmacion(null);
                              setACambiar(activo);
                            }}
                          >
                            {activo.activo ? (
                              <Ban className="size-4 text-rose-500" aria-hidden="true" />
                            ) : (
                              <RotateCcw className="size-4 text-emerald-600" aria-hidden="true" />
                            )}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {total > lista.length ? (
            <p className="text-xs text-ink-400">
              Mostrando {lista.length} de {total} activos. Usa el buscador para acotar.
            </p>
          ) : null}
        </CardContent>
      </Card>

      {dialogo ? (
        <ActivoDialogo
          activo={dialogo.activo}
          categorias={categorias}
          guardando={guardando}
          error={errorDialogo}
          onGuardar={guardar}
          onCerrar={() => setDialogo(null)}
        />
      ) : null}

      {detalleId !== null ? (
        <ActivoDetalle activoId={detalleId} onCerrar={() => setDetalleId(null)} />
      ) : null}

      {aCambiar ? (
        <ConfirmDialog
          open
          tone={aCambiar.activo ? 'danger' : 'primary'}
          title={aCambiar.activo ? 'Marcar activo en desuso' : 'Reactivar activo'}
          description={
            aCambiar.activo
              ? 'El activo y sus sub-activos se conservan, pero dejan de contar para el mantenimiento.'
              : 'El activo vuelve a contar para el mantenimiento preventivo.'
          }
          confirmLabel={aCambiar.activo ? 'Marcar en desuso' : 'Reactivar'}
          loading={cambiarEstado.isPending}
          onClose={() => setACambiar(null)}
          onConfirm={confirmarEstado}
        >
          <div className="flex flex-col gap-3">
            <p className="text-sm text-ink-600">
              <strong className="text-ink-900">{aCambiar.nombre}</strong>{' '}
              <span className="font-mono text-xs">{aCambiar.codigo}</span>
            </p>

            {aCambiar.activo && aCambiar.sub_activos > 0 ? (
              <Alert variant="warning">
                Tiene <strong>{formatNumber(aCambiar.sub_activos)} sub-activos</strong>. No se
                borran: siguen ahí para cuando lo reactives.
              </Alert>
            ) : null}

            {errorConfirmacion ? (
              <Alert variant="error" title="No se pudo cambiar el estado">
                {errorConfirmacion}
              </Alert>
            ) : null}
          </div>
        </ConfirmDialog>
      ) : null}
    </div>
  );
}
